/**
 * Paper Trading Order Ticket Validation, Sizing & Toast Formatting.
 *
 * Implements input validation for Market, Limit, and Stop orders, lot size calculations,
 * and formats toast notifications for filled, resting, split, and margin-blocked orders.
 */

export interface PaperModalValidationResult {
  valid: boolean;
  error?: string;
  limitPrice: number | null;
  triggerPrice: number | null;
}

/**
 * Validates modal order inputs based on order type (MARKET / LIMIT / SL-M / SL-L).
 */
export function validatePaperModalInputs(
  orderType: string,
  priceVal?: string | number | null,
  triggerVal?: string | number | null,
): PaperModalValidationResult {
  let limitPrice: number | null = null;
  let triggerPrice: number | null = null;

  if (orderType === "LIMIT" || orderType === "SL-L") {
    const p =
      typeof priceVal === "number"
        ? priceVal
        : parseFloat(String(priceVal || 0));
    if (!p || isNaN(p) || p <= 0) {
      return {
        valid: false,
        error: "Enter a valid limit price",
        limitPrice: null,
        triggerPrice: null,
      };
    }
    limitPrice = p;
  }

  if (orderType === "SL-M" || orderType === "SL-L") {
    const trg =
      typeof triggerVal === "number"
        ? triggerVal
        : parseFloat(String(triggerVal || 0));
    if (!trg || isNaN(trg) || trg <= 0) {
      return {
        valid: false,
        error: "Enter a valid trigger price",
        limitPrice,
        triggerPrice: null,
      };
    }
    triggerPrice = trg;
  }

  return { valid: true, limitPrice, triggerPrice };
}

/**
 * Calculates total order quantity given lot count and contract lot size.
 */
export function calculatePaperOrderQty(
  lots: number | string | null | undefined,
  lotSize: number | string | null | undefined,
): number {
  const nLots = parseInt(String(lots || 1), 10);
  const nLotSize = parseInt(String(lotSize || 1), 10);
  return (
    Math.max(1, isNaN(nLots) ? 1 : nLots) *
    Math.max(1, isNaN(nLotSize) ? 1 : nLotSize)
  );
}

export interface PaperOrderFill {
  side: string;
  qty?: number;
  price: number;
  blocked?: number;
}

export interface PaperOrderResponse {
  status: string;
  fills?: PaperOrderFill[];
  split?: boolean;
  orders?: any[];
}

/**
 * Formats toast message for paper order placement or fill.
 */
export function formatPaperOrderToast(
  data: PaperOrderResponse,
  orderType: string,
  side: string,
  qty: number,
  symbol: string,
  formatNumberFn?: (n: number) => string,
): string {
  const format = formatNumberFn || ((n: number) => n.toFixed(2));
  const splitMsg =
    data.split && Array.isArray(data.orders)
      ? ` (split into ${data.orders.length} — freeze qty)`
      : "";

  if (
    data.status === "filled" &&
    Array.isArray(data.fills) &&
    data.fills.length > 0
  ) {
    const f = data.fills[0];
    const filledQty = data.fills.reduce((s, x) => s + (x.qty || 0), 0);
    const blockedMsg = f.blocked ? ` (${f.blocked} blocked — margin)` : "";
    return `${orderType} FILLED: ${f.side} ${filledQty} ${symbol} @ ${format(f.price)}${splitMsg}${blockedMsg}`;
  }

  return `${orderType} order placed: ${side} ${qty} ${symbol}${splitMsg}`;
}
